import { Delete, KeyboardArrowDown } from '@mui/icons-material';
import { Box, Card, CardContent, Collapse, IconButton, Stack } from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import PropTypes from 'prop-types';
import React, { PureComponent, useEffect, useState } from 'react';
import sortableItem from '../../decorators/sortable-item.js';
import ContentPreview from '../content-preview/index.js';
import SchemaField from '../schema-field.jsx';

const DEFAULT_TRANSITION_TIMEOUT = 300;

function ArrayFieldItemCard({
  isDragging,
  isPlaceholder,
  disabled,
  readOnly,
  isCollapsed,
  connectDragSource,
  formName,
  schema,
  schemaPath,
  config,
  widgets,
  locale,
  resolveRef,
  forms,
  handleRemove,
  handleToggle,
  path,
  children
}) {
  const header = (
    <header>
      <Stack
        alignItems="center"
        flexDirection="row"
        justifyContent="space-between"
        sx={{
          backgroundColor: blueGrey[50],
          cursor: !disabled ? 'move' : 'initial',
          p: 0.5
        }}
      >
        <IconButton data-testid="array-field-item-toggle" onClick={handleToggle} size="small">
          <KeyboardArrowDown
            size="16px"
            sx={{
              transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
              transition: `${0}ms ease all`
            }}
          />
        </IconButton>
        <Box flex="1" overflow="hidden">
          <ContentPreview
            formName={formName}
            path={path}
            schema={schema}
            schemaPath={schemaPath}
            config={config}
            widgets={widgets}
            locale={locale}
            resolveRef={resolveRef}
            forms={forms}
          />
        </Box>
        <IconButton
          data-testid="array-field-item-remove"
          onClick={handleRemove}
          disabled={disabled || readOnly}
          size="small"
        >
          <Delete fontSize="small" />
        </IconButton>
      </Stack>
    </header>
  );

  // This is insane. Required because otherwise the full length of the
  // collapsible form will take up DnD space, even when it is collapsed.
  // Related: https://github.com/react-dnd/react-dnd/issues/832
  // Trust me, I tried every other possible solution first.
  // Alternatively, just use `display: isCollapsed ? 'none' : ''` on the Collapsible,
  // but you will lose the closing transition.
  const [hideIt, setHideIt] = useState(isCollapsed);
  const [openIt, setOpenIt] = useState(!isCollapsed);
  const [readyToAnimate, setReadyToAnimate] = useState(false);

  // Prevent showing collapse animation if initial state is collapsed
  useEffect(() => {
    setTimeout(() => {
      setReadyToAnimate(true);
    }, [0]);
  }, []);

  useEffect(() => {
    if (isCollapsed === true && !openIt) {
      return;
    }

    if (isCollapsed === false && !hideIt) {
      setOpenIt(true);
      return;
    }

    if (isCollapsed === true && !hideIt) {
      setOpenIt(false);
      return;
    }

    if (isCollapsed === false) {
      setHideIt(false);
      return;
    }

    if (isCollapsed === true) {
      setHideIt(false);
    }
  }, [hideIt, isCollapsed, openIt]);

  const transitionTimeout = React.useMemo(() => {
    const configured = config.get('transitionTimeout');
    return configured ?? DEFAULT_TRANSITION_TIMEOUT;
  }, [config]);

  const [hideDuringDrag, setHideDuringDrag] = useState(false);

  // This works around the fact that we do not want the placeholder to be visible
  // while dragging but we want it to be visible when the drag preview is captured.
  useEffect(() => {
    if (isDragging) {
      const timeout = setTimeout(() => setHideDuringDrag(true), 1000);
      return () => clearTimeout(timeout);
    }
    setHideDuringDrag(false);
  }, [isDragging]);

  return (
    <Card
      variant="outlined"
      data-testid={`array-field-item-${path}`}
      className={isDragging ? 'array-field-item-dragging' : ''}
      sx={{
        mt: 1,
        opacity: hideDuringDrag ? 0 : 1
      }}
    >
      {disabled || readOnly ? header : connectDragSource(header)}
      <Collapse
        in={openIt}
        style={{
          display: hideIt ? 'none' : ''
        }}
        timeout={transitionTimeout}
        sx={{ transition: readyToAnimate ? undefined : 'none !important' }}
        addEndListener={() => {
          if (isCollapsed === true && !openIt) {
            setTimeout(() => {
              setHideIt(true);
            }, transitionTimeout);
          }
        }}
      >
        <CardContent sx={{ '&:last-child': { paddingBottom: 2 } }}>{children}</CardContent>
      </Collapse>
    </Card>
  );
}

function ArrayFieldItemForm({
  disabled,
  readOnly,
  formName,
  schema,
  schemaPath,
  config,
  widgets,
  locale,
  resolveRef,
  forms,
  path,
  context
}) {
  return (
    <SchemaField
      resolveRef={resolveRef}
      schema={schema}
      schemaPath={schemaPath}
      formName={formName}
      path={path}
      config={config}
      context={context}
      widgets={widgets}
      disabled={disabled}
      readOnly={readOnly}
      locale={locale}
      forms={forms}
    />
  );
}

class ArrayFieldItem extends PureComponent {
  static propTypes = {
    resolveRef: PropTypes.func.isRequired,
    formName: PropTypes.string.isRequired,
    schema: PropTypes.object.isRequired,
    schemaPath: PropTypes.array.isRequired,
    path: PropTypes.string.isRequired,
    config: PropTypes.object.isRequired,
    forms: PropTypes.object,
    context: PropTypes.object,
    widgets: PropTypes.object,
    disabled: PropTypes.bool,
    readOnly: PropTypes.bool,
    locale: PropTypes.string,

    // Array related:
    index: PropTypes.number.isRequired,
    arrayKey: PropTypes.number.isRequired,
    isCollapsed: PropTypes.bool.isRequired,
    removeItem: PropTypes.func.isRequired,
    toggleItem: PropTypes.func.isRequired,
    fieldAction: PropTypes.func.isRequired,
    isDragging: PropTypes.bool.isRequired,
    isPlaceholder: PropTypes.bool,
    connectDragSource: PropTypes.func.isRequired,
    connectDropTarget: PropTypes.func.isRequired,
    connectDragPreview: PropTypes.func.isRequired,
    onBlur: PropTypes.func
  };

  handleRemove = (event) => {
    event.preventDefault();
    const { removeItem, index, onBlur } = this.props;
    removeItem(index);
    onBlur();
  };

  handleToggle = (event) => {
    event.preventDefault();
    const { toggleItem, arrayKey } = this.props;
    toggleItem(arrayKey);
  };

  setDomNode = (node) => {
    this.domNode = node;
  };

  render() {
    const { path, index, connectDropTarget, connectDragPreview, widgets, config, disabled, isPlaceholder } = this.props;
    const currentPath = `${path}[${index}]`;

    const Wrapper = widgets?.[config.get('itemWrapper')];

    if (Wrapper) {
      return (
        <Wrapper
          {...this.props}
          setDomNode={this.setDomNode}
          onRemoveItem={this.handleRemove}
          onToggleItem={this.handleToggle}
          parentPath={path}
          path={currentPath}
          disabled={disabled}
        >
          <ArrayFieldItemForm {...this.props} path={currentPath} />
        </Wrapper>
      );
    }

    return connectDragPreview(
      connectDropTarget(
        // Fix DnD preview in Chrome, even without the hack above
        <div ref={this.setDomNode}>
          <ArrayFieldItemCard
            {...this.props}
            handleRemove={this.handleRemove}
            handleToggle={this.handleToggle}
            path={currentPath}
          >
            {!isPlaceholder ? (
              <div className="flex flex-col gap-6 py-2">
                <ArrayFieldItemForm {...this.props} path={currentPath} />
              </div>
            ) : null}
          </ArrayFieldItemCard>
        </div>
      ),
      {
        captureDraggingState: true
      }
    );
  }
}

export default sortableItem(ArrayFieldItem);
